<script setup lang="ts">
import {
  createDefaultColorOptions,
  createMarkupEditorToolbar,
  createMarkupEditorToolStyles,
  getEditorDefaults
} from '@pqina/pintura'
import localeDeDE, {
  MarkupEditor as localeMarkupEditorDeDE
} from '@pqina/pintura/locale/de_DE/core/de_DE.js'
import localeAnnotateDeDE from '@pqina/pintura/locale/de_DE/annotate/de_DE.js'
import localeCropDeDE from '@pqina/pintura/locale/de_DE/crop/de_DE.js'
import localeFilterDeDE from '@pqina/pintura/locale/de_DE/filter/de_DE.js'
import localeFinetuneDeDE from '@pqina/pintura/locale/de_DE/finetune/de_DE.js'
import localeResizeDeDE from '@pqina/pintura/locale/de_DE/resize/de_DE.js'
import moment from 'moment'
import { enabled } from 'virtual:nuxt-pwa-configuration'
import 'moment/dist/locale/de'

const { siteName, appVersion } = useAppConfig()

const showNotificationDialog = ref(false)
const notifications = useNotifications()
const notificationStore = useNotificationStore()

useHead({
  titleTemplate: (titleChunk) => {
    return titleChunk ? `${titleChunk} - ${siteName}` : siteName
  },
  meta: [
    { name: 'version', content: `${appVersion}` },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
    }
  ]
})

const tabItems = computed(() => [
  {
    id: 0,
    route: `/dashboard${route?.query?.date ? '?date=' + route.query.date : ''}`,
    label: t('global.userMenu.dashboard'),
    icon: 'pi pi-home'
  },
  {
    id: 1,
    route: `/events${route?.query?.date ? '?date=' + route.query.date : ''}`,
    label: t('global.userMenu.appointments'),
    icon: 'pi pi-calendar',
    badge:
      !user.isLoggedInAsContact && uncompletedChecklistsCount.value > 0
        ? uncompletedChecklistsCount.value
        : null
  },
  {
    id: 2,
    route: `/chat${route?.query?.date ? '?date=' + route.query.date : ''}`,
    label: 'Chat',
    icon: 'pi pi-comments',
    badge: totalUnreadMessages.value > 0 ? totalUnreadMessages.value : null
  },
  {
    id: 3,
    route: '#',
    label: t('global.userMenu.menu'),
    icon: 'pi pi-bars',
    command: () => openSidebar()
  }
])
const { setLocale } = useL10n()
const { locale, loadLocaleMessages } = useI18n()
const { $pwa } = useNuxtApp()
const { t } = useL10n()
const route = useRoute()
const toast = useToast()
const user = useUserStore()
const languageStore = useLanguageStore()
const customFieldStore = useCustomFieldStore()
const languageSelectionStore = useLanguageSelectionStore()
const { uncompletedChecklistsCount, countUncompletedChecklists } = useUncompletedChecklists()
const chatStore = useChatStore()

const totalUnreadMessages = computed(() => {
  let total = 0
  chatStore.unreadMessages?.forEach((messages) => {
    total += messages?.length || 0
  })
  return total
})

watch(
  () => route.path,
  () => {
    if (chatStore.selectedChannel && !route.path.includes('/chat')) {
      chatStore.clearChannel()
    }
  }
)

const loadAndSetLanguage = async (languageCode: string) => {
  const code = languageCode as 'en' | 'de' | 'bg' | 'ro' | 'sl'
  await loadLocaleMessages(code)

  setLocale(code)
  locale.value = code

  languageSelectionStore.selectedLanguage = code
  chatStore.currentLanguage = code

  moment.locale(code === 'de' ? 'de' : 'en')
}
// Sidebar
const sidebarVisible = ref(false)

const openSidebar = () => {
  sidebarVisible.value = true
}

const mainMenuItems = computed(() => {
  const items = [
    {
      label: t('global.mainMenu.dashboard'),
      icon: 'pi pi-home',
      command: () => navigateAndCloseSidebar('/dashboard')
    },
    {
      label: t('components.notificationPanel.title'),
      icon: 'pi pi-bell',
      command: () => {
        showNotificationDialog.value = true
      }
    },
    {
      label: t('global.mainMenu.appointments'),
      icon: 'pi pi-calendar',
      command: () => navigateAndCloseSidebar('/events'),
      badge:
        !user.isLoggedInAsContact && uncompletedChecklistsCount.value > 0
          ? uncompletedChecklistsCount.value
          : null
    },
    {
      label: t('global.mainMenu.invoices'),
      icon: 'pi pi-file',
      command: () => navigateAndCloseSidebar('/invoices')
    },
    {
      label: t('global.mainMenu.chat'),
      icon: 'pi pi-comments',
      command: () => navigateAndCloseSidebar('/chat'),
      badge: totalUnreadMessages.value > 0 ? totalUnreadMessages.value : null
    },
    {
      label: t('global.mainMenu.contracts'),
      icon: 'pi pi-file-pdf',
      command: () => navigateAndCloseSidebar('/proposals')
    }
  ]

  items.push(
    {
      label: t('global.mainMenu.changeLanguage'),
      icon: 'pi pi-language',
      command: () => languageSelectionStore.showLanguageDialog()
    },
    {
      label: t('global.mainMenu.reload'),
      icon: 'pi pi-refresh',
      command: () => {
        window.location.reload()
      }
    },
    {
      label: t('global.mainMenu.logout'),
      icon: 'pi pi-sign-out',
      command: () => {
        user.logout()
        sidebarVisible.value = false
      }
    }
  )

  return items
})

// Language selection dialog

const navigateAndCloseSidebar = (route: string) => {
  sidebarVisible.value = false
  navigateTo(route)
}

const applyDefaultColor = () => {
  useCssVar('--primary-color-50').value = '#DFF1C9'
  useCssVar('--primary-color-100').value = '#D5ECB9'
  useCssVar('--primary-color-200').value = '#C2E499'
  useCssVar('--primary-color-300').value = '#AFDB78'
  useCssVar('--primary-color-400').value = '#9CD358'
  useCssVar('--primary-color-500').value = '#89CA38'
  useCssVar('--primary-color-600').value = '#6B9F2A'
  useCssVar('--primary-color-700').value = '#4E731F'
  useCssVar('--primary-color-800').value = '#304713'
  useCssVar('--primary-color-900').value = '#121B07'
  useCssVar('--primary-color-950').value = '#030401'
  useCssVar('--p-primary-50').value = '#DFF1C9'
  useCssVar('--p-primary-100').value = '#D5ECB9'
  useCssVar('--p-primary-200').value = '#C2E499'
  useCssVar('--p-primary-300').value = '#AFDB78'
  useCssVar('--p-primary-400').value = '#9CD358'
  useCssVar('--p-primary-500').value = '#89CA38'
  useCssVar('--p-primary-600').value = '#6B9F2A'
  useCssVar('--p-primary-700').value = '#4E731F'
  useCssVar('--p-primary-800').value = '#304713'
  useCssVar('--p-primary-900').value = '#121B07'
  useCssVar('--p-primary-950').value = '#030401'
  useCssVar('--p-primary-color').value = '#89CA38'
  useCssVar('--primary-color').value = '#89CA38'
  useCssVar('--surface-500').value = '#f5f5f5'
}

const navigateToTab = (route: string) => {
  if (route === '#') {
    openSidebar()
  } else {
    navigateTo(route)
  }
}

watch(
  () => [user.isLoggedInAsContact, user.isLoggedIn],
  async ([newValueContact, newValueUser]) => {
    if (newValueContact || newValueUser) {
      const permission = await notifications.requestPermission()
      if (permission === 'granted') {
        const subscription = await notifications.checkExistingSubscription()
        if (!subscription) notifications.subscribe()
      }
    }
  }
)

watch(
  () => [user.isLoggedInAsContact, user.isLoggedIn],
  async ([newValueContact, newValueUser]) => {
    if (newValueContact || newValueUser) {
      if (!route?.name?.toString()?.includes('chat')) chatStore.loadChannels()
    }
    if (newValueUser) {
      countUncompletedChecklists()
      const contactUserRelation = await useApi(
        `contact-user-relation?filter[user_id]=${user.user.id}`,
        'GET'
      )
      user.contactUserRelation = contactUserRelation?.data?.[0]?.knr
      const contact = await useApi(
        `contacts/${contactUserRelation?.data?.[0]?.knr}?include=custom_fields_0`,
        'GET'
      )
      const userLanguage = contact?.data?.custom_fields_0?.v_30
      if (userLanguage && userLanguage.length > 1) {
        await loadAndSetLanguage(userLanguage)
      } else {
        await Promise.all([
          useApi(`contacts/${contactUserRelation?.data?.[0]?.knr}`, 'PUT', {
            'custom_fields_0.v_30': locale.value
          }),
          loadAndSetLanguage(locale.value)
        ])
      }
    } else if (newValueContact) {
      const contactResponse = await useApi(
        `css/contacts/${user.contact.id}?include=custom_fields_0`,
        'GET'
      )
      const userLanguage = contactResponse?.data?.custom_fields_0?.v_30
      if (userLanguage && userLanguage.length > 1) {
        await loadAndSetLanguage(userLanguage)
      } else {
        await Promise.all([
          useApi(`css/contacts/${user.contact.id}`, 'PUT', {
            'custom_fields_0.v_30': locale.value
          }),
          loadAndSetLanguage(locale.value)
        ])
      }
    }
  },
  { immediate: true }
)

onMounted(async () => {
  notificationStore.initializeNotifications()
  applyDefaultColor()
  provideGlobalState()

  if (enabled && 'serviceWorker' in navigator) {
    try {
      await navigator.serviceWorker.ready
    } catch (error) {
      toast.add({ severity: 'warn', detail: 'Offline features may not work' })
    }
  }

  await user.initializeUserName()

  const mandantSettings = (await useApi('css/tenant/settings', 'GET')) as { data: any }
  customFieldStore.setData(mandantSettings?.data ?? {})

  document.addEventListener('mousedown', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-select')) {
      event.preventDefault()
    }
  })

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-select')) {
      event.preventDefault()
    }
  })

  document.addEventListener('mousedown', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-multiselect')) {
      event.preventDefault()
    }
  })

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-multiselect')) {
      event.preventDefault()
    }
  })

  menuItems.value = [
    {
      label: t('global.userMenu.reload'),
      icon: 'pi pi-refresh',
      command: () => {
        $pwa?.updateServiceWorker(true)
      }
    },
    {
      label: t('global.userMenu.logout'),
      icon: 'pi pi-sign-out',
      command: () => {
        user.calendarCategories = null
        user.setHasSupervisor(null)

        user.logout()
        navigateTo('/welcome')
      }
    }
  ]

  imageEditorProps.value = getEditorDefaults()

  if (languageStore.language === 'german') {
    moment.locale('de')
    imageEditorProps.value.locale = {
      ...localeDeDE,
      ...localeMarkupEditorDeDE,
      ...localeAnnotateDeDE,
      ...localeCropDeDE,
      ...localeFilterDeDE,
      ...localeFinetuneDeDE,
      ...localeResizeDeDE
    }
  }

  imageEditorProps.value.enableZoomControls = false

  imageEditorProps.value.utils = ['annotate', 'crop', 'filter', 'finetune', 'resize']

  imageEditorProps.value.markupEditorToolbar = createMarkupEditorToolbar([
    'ellipse',
    'arrow',
    'rectangle',
    'line',
    'text',
    'preset'
  ])

  imageEditorProps.value.markupEditorToolStyles = createMarkupEditorToolStyles({
    marker: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    ellipse: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    rectangle: {
      backgroundColor: createDefaultColorOptions().transparent,
      strokeColor: createDefaultColorOptions().red,
      strokeWidth: 12
    },
    arrow: {
      strokeWidth: 12
    },
    text: {
      fontSize: 48
    }
  })
})
</script>
<template>
  <div>
    <NuxtLayout :key="languageStore.language">
      <VitePwaManifest />
      <NuxtPage />
      <Toast />

      <Dialog
        v-model:visible="showNotificationDialog"
        modal
        :header="t('components.notificationPanel.title')"
        :style="{ width: '90vw', maxWidth: '600px' }"
        class="notification-dialog"
      >
        <NotificationPanel />
      </Dialog>

      <div
        v-if="
          route?.name?.toString()?.includes('dashboard') ||
          route?.name?.toString()?.includes('events') ||
          route?.name?.toString()?.includes('chat') ||
          route?.name?.toString()?.includes('proposals') ||
          route?.name?.toString()?.includes('invoices')
        "
        class="relative mt-14 z-10 pt-3"
      >
        <Card
          class="!p-0 fixed bottom-0 left-0"
          :pt="{ root: { class: 'w-full' }, body: { class: '!p-0 !w-full' } }"
        >
          <template #content>
            <div class="tabs-container pb-[0.75rem]">
              <Tabs
                :value="
                  route?.name?.toString()?.includes('events')
                    ? 1
                    : route?.name?.toString()?.includes('chat')
                      ? 2
                      : 0
                "
              >
                <TabList>
                  <Tab
                    v-for="tab in tabItems"
                    :key="tab.id"
                    :value="tab.id"
                    :class="{ 'menu-button': tab.id === 3 }"
                    @click="navigateToTab(tab.route)"
                  >
                    <div class="tab-icon-wrapper">
                      <i :class="tab.icon" />
                      <span v-if="tab.badge && tab.badge > 0" class="tab-badge !text-white">
                        {{ tab.badge }}
                      </span>
                    </div>
                    <span class="tab-label">{{ tab.label }}</span>
                  </Tab>
                </TabList>
              </Tabs>
            </div>
          </template>
        </Card>
      </div>

      <!-- Sidebar Menu -->
      <Drawer v-model:visible="sidebarVisible" position="right" :pt="{ root: { class: 'w-80' } }">
        <template #header>
          <div class="flex flex-col gap-2 items-start">
            <Image src="/Logo.png" width="75" :pt="{ image: { class: 'object-contain' } }" />
            <p class="font-semibold">
              {{ user.userName }}
            </p>
          </div>
        </template>
        <div class="sidebar-content">
          <div class="menu-divider"></div>

          <div class="menu-items">
            <a
              v-for="item in mainMenuItems"
              :key="item.label || 'separator'"
              class="menu-item"
              @click="item.command"
            >
              <span
                v-if="item.label === t('global.mainMenu.changeLanguage')"
                class="menu-item-content"
              >
                <span class="menu-icon-wrapper">
                  <i :class="item.icon" class="menu-icon"></i>
                </span>
                <span class="menu-label">{{ item.label }}</span>
                <Button
                  class="language-button"
                  text
                  rounded
                  @click.stop="languageSelectionStore.showLanguageDialog"
                />
              </span>

              <span v-else class="menu-item-content">
                <span class="menu-icon-wrapper">
                  <i :class="item.icon" class="menu-icon"></i>
                  <span v-if="item.badge && item.badge > 0" class="menu-badge">
                    {{ item.badge }}
                  </span>
                </span>
                <span class="menu-label">{{ item.label }}</span>
              </span>
            </a>
          </div>
        </div>
      </Drawer>

      <LanguageSelectionDialog />
    </NuxtLayout>
  </div>
</template>
<style lang="scss">
body {
  background-color: var(--surface-500);
}
div {
  line-height: normal;
}
.p-card {
  background-color: white !important;
  border-radius: 0.25rem !important;
  padding: 0.75rem !important;
}
.p-card-body {
  padding: 4px !important;
}
.p-card-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 0;
  padding-bottom: 0;
}
.p-component {
  font-size: 0.875rem;
}
.p-inputtext::placeholder,
.p-textarea::placeholder,
.p-placeholder {
  color: var(--p-surface-400) !important;
  font-size: 14px;
}
.p-multiselect .p-multiselect-label.p-placeholder {
  color: var(--p-surface-400) !important;
  font-size: 14px;
}
.error {
  color: crimson;
  font-weight: 500;
}
.p-select {
  z-index: 1 !important;
}
.p-select-option-label {
  white-space: pre-line;
}
.p-toast {
  position: fixed;
  top: 0;
  right: 0;
  left: 20px;
  z-index: 1000;
  width: 80% !important;
}
.image-editor {
  height: 24rem;
}
.fieldTitle {
  color: var(--primary-color);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.buttonHeader {
  background-color: var(--primary-600) !important;
  border: 0 !important;
  padding: 0.5rem 1.8rem !important;
}
.p-button {
  .p-button-label {
    color: white;
    font-weight: 300;
  }
  .p-button-icon {
    color: white;
  }
}
.p-button-secondary {
  .p-button-label {
    color: #475569 !important;
  }
  .p-button-icon {
    color: #475569 !important;
  }
}
.p-menu-item-label,
.p-menu-item-icon {
  color: var(--text-color) !important;
}
.p-iftalabel {
  input {
    border-radius: 14px;
    padding: 1rem;
    margin-top: 0.1rem;
  }
  div {
    margin-top: 0.1rem;
  }
  label {
    margin-top: 0.3rem;
    margin-left: 0.4rem;
  }
}
.groupTitle {
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: black;
}
.selectInput {
  width: 100%;
  height: 3.5rem;
  align-items: center;

  .p-datepicker-input,
  .p-inputnumber-input,
  button {
    height: 100%;
  }
}
.checkboxGroup {
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.PinturaRoot .PinturaButtonExport {
  background-color: var(--primary-600) !important;
  padding: 0.4rem 1.25rem !important;

  .PinturaButtonInner {
    color: white !important;
  }
}

.PinturaButtonIconOnly {
  background-color: var(--primary-600) !important;
  border-color: white !important;
  padding: 0.4rem 1.25rem !important;

  .PinturaButtonInner {
    color: white !important;
  }
}
.PinturaButton[disabled] {
  background-color: #a8cfd5 !important;
  filter: none !important;

  .PinturaButtonInner {
    color: white !important;
  }

  svg {
    opacity: 0.75 !important;
  }
}
.submitButton {
  width: 100% !important;
  padding: 1.25rem !important;
  border-radius: 30px !important;
  margin-top: 1.25rem !important;
}
.signaturePad,
canvas {
  border-style: solid;
  border-width: 1px;
  border-color: #d1d5db;
  background-color: white;
  border-radius: 14px;
  width: 100% !important;
}

.p-multiselect-filter {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 0.875rem;
}

.p-multiselect-dropdown,
.p-select-dropdown {
  width: unset;
  min-width: 3rem;
  padding-right: 0.5rem;
  pointer-events: auto;
}
.changeInputTypeButton {
  border: none !important;
  background-color: transparent !important;
  padding: 0 !important;

  .p-button-label {
    color: var(--primary-color) !important;
  }
  &:hover {
    background-color: transparent !important;
    border: none !important;
  }
}
.todaysAppointments,
.todaysReports {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1rem;
  color: #4b5563 !important;
}

.tabs-container {
  width: 100%;
  overflow: hidden;
}
.tabs-container .p-tab {
  width: 25%;
  min-height: 68px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
  padding: 0.4rem 0.25rem;
  background-color: #ffffff;
  border-width: 3px 0 0 0;
  span {
    color: #64748b;
    font-weight: 600;
  }

  i {
    font-size: 1.4rem;
  }
}

.tab-label {
  font-size: 0.68rem;
  line-height: 1.1;
  text-align: center;
}

.p-tab-active i {
  color: var(--primary-600) !important;
}
.p-tab-active span {
  color: var(--primary-color) !important;
}
.p-select {
  outline: none;
}
.p-select:focus {
  outline: none;
}
.p-multiselect {
  outline: none;
}
.p-multiselect:focus {
  outline: none;
}
.p-textarea,
.p-inputtext {
  font-size: 14px !important;
}
.p-tab-active {
  color: var(--primary-600) !important;
  border-color: var(--primary-600) !important;

  span {
    color: var(--primary-600) !important;
  }
}

// Menu button: keep consistent with the other tabs (icon + label)
.tabs-container .p-tab.menu-button {
  i {
    font-size: 1.4rem !important;
  }
}

// Tab icon wrapper and badge styling
.tab-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tab-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ef4444;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  min-width: 18px;
  z-index: 10;
}

// Sidebar styling
:deep(.p-sidebar) {
  .p-sidebar-header {
    background-color: var(--primary-color);
    color: white;
    padding: 1rem 1.5rem;
    font-size: 1.25rem;
    font-weight: bold;
  }

  .p-sidebar-close {
    color: white !important;
  }
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.profile-section {
  padding: 1.5rem;
  background-color: #f8fafc;
}

.profile-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.profile-avatar {
  background-color: var(--primary-color) !important;
  color: white !important;
}

.profile-info {
  flex: 1;
}

.profile-name {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  color: #1f2937;
}

.menu-divider {
  height: 1px;
  background-color: #e5e7eb;
}

.menu-items {
  flex: 1;
  overflow-y: auto;
}

.menu-item {
  display: block;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9fafb;
  }

  &.separator {
    padding: 0;
    height: 8px;
    background-color: #f3f4f6;
    border-bottom: none;
    pointer-events: none;
  }

  &:last-child {
    border-bottom: none;
  }
}

.menu-item-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.menu-icon-wrapper {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  border-radius: 0.5rem;
  position: relative;
}

.menu-icon {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.menu-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 20px;
}

.menu-label {
  font-size: 1rem;
  font-weight: 500;
  flex: 1;
}

.language-button {
  margin-left: auto;
  min-width: 2rem !important;
  width: 2rem !important;
  height: 2rem !important;
}
</style>
